import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiPhone, FiMail, FiAlertCircle } from 'react-icons/fi';
import { getClients, createClient, updateClient, deleteClient } from '../services/clientService';
import ClientModal from '../components/ClientModal';
import ConfirmModal from '../components/ConfirmModal';
import Notification from '../components/Notification';

const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentClient, setCurrentClient] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [notification, setNotification] = useState(null);

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get clients directly from database
      const data = await getClients();
      setClients(data);
    } catch (err) {
      setError('Failed to fetch clients. Please try again.');
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-secondary-900">Clients</h1>
        <button 
          onClick={() => {
            setCurrentClient(null);
            setIsModalOpen(true);
          }}
          className="btn btn-primary flex items-center"
        >
          <FiPlus className="mr-1" />
          Add Client
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
        </div>
      ) : error ? (
        <div className="bg-danger bg-opacity-10 border border-danger text-danger px-6 py-4 rounded-lg flex items-center">
          <FiAlertCircle className="mr-2" size={20} />
          <span>{error}</span>
          <button 
            onClick={fetchClients}
            className="ml-auto btn btn-danger btn-sm"
          >
            Try Again
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clients.map((client) => (
            <div key={client.id} className="bg-white rounded-lg shadow-card overflow-hidden">
              <div className="p-5">
                <div className="flex justify-between items-start">
                  <h3 className="text-lg font-medium text-secondary-900">{client.name}</h3>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => {
                        setCurrentClient(client);
                        setIsModalOpen(true);
                      }}
                      className="text-secondary-600 hover:text-secondary-900"
                    >
                      <FiEdit2 size={18} />
                    </button>
                    <button 
                      onClick={() => {
                        setClientToDelete(client);
                        setIsConfirmModalOpen(true);
                      }}
                      className="text-danger hover:text-red-700"
                    >
                      <FiTrash2 size={18} />
                    </button>
                  </div>
                </div>
                
                <div className="mt-4 space-y-2">
                  <div className="flex items-center text-sm text-secondary-500">
                    <FiMail className="mr-2" />
                    <a href={`mailto:${client.email}`} className="hover:text-primary-600">
                      {client.email}
                    </a>
                  </div>
                  <div className="flex items-center text-sm text-secondary-500">
                    <FiPhone className="mr-2" />
                    <a href={`tel:${client.phone}`} className="hover:text-primary-600">
                      {client.phone}
                    </a>
                  </div>
                </div>
                
                <div className="mt-5 pt-4 border-t border-secondary-100">
                  <div className="flex justify-between text-sm">
                    <span className="text-secondary-500">Active Tasks</span>
                    <span className="font-medium text-secondary-900">3</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Client Modal */}
      <ClientModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        client={currentClient}
        onSave={handleSaveClient}
      />
      
      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleDeleteClient}
        title="Delete Client"
        message={`Are you sure you want to delete ${clientToDelete?.name}? This action cannot be undone.`}
      />
      
      {/* Notification */}
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
  
  // Handle saving client (create or update)
  async function handleSaveClient(formData) {
    try {
      if (currentClient) {
        // Update existing client
        const updatedClient = await updateClient(currentClient.id, formData);
        
        // Update local state
        setClients(prevClients => 
          prevClients.map(c => c.id === updatedClient.id ? updatedClient : c)
        );
        
        // Show success notification
        setNotification({
          type: 'success',
          message: `Client "${updatedClient.name}" updated successfully`
        });
      } else {
        // Create new client
        const newClient = await createClient(formData);
        
        // Update local state
        setClients(prevClients => [newClient, ...prevClients]);
        
        // Show success notification
        setNotification({
          type: 'success',
          message: `Client "${newClient.name}" created successfully`
        });
      }
      
      setIsModalOpen(false);
    } catch (err) {
      console.error('Error saving client:', err);
      setNotification({
        type: 'error',
        message: 'Failed to save client. Please try again.'
      });
    }
  }
  
  // Handle deleting client
  async function handleDeleteClient() {
    if (!clientToDelete) return;
    
    try {
      // Delete client directly from database
      await deleteClient(clientToDelete.id);
      
      // Store name for notification
      const clientName = clientToDelete.name;
      
      // Remove client from state
      setClients(prevClients => 
        prevClients.filter(c => c.id !== clientToDelete.id)
      );
      
      setIsConfirmModalOpen(false);
      setClientToDelete(null);
      
      // Show success notification
      setNotification({
        type: 'success',
        message: `Client "${clientName}" deleted successfully`
      });
    } catch (err) {
      console.error('Error deleting client:', err);
      setNotification({
        type: 'error',
        message: 'Failed to delete client. Please try again.'
      });
    }
  }
};

export default ClientsPage;